import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import useFetch from "../../../hooks/useFetch";
import { useAppDispatch } from "../../../store/store";
import { appointmentSchema, TAppointment } from "./schema";
import TriageSelectBeneficiary from "../FamilyGroup/TriageSelectBeneficiary/TriageSelectBeneficiary";
import Insurance from "./Steps/Insurance";
import PagesContainer from "../../../components/UI/PagesContainer";
import TriageEmergencyDepartment from "../../../services/triageEmergencyDepartment.service";
import { notificationSliceActions } from "../../../store/slices/notification";
import {
  TriageContextProvider,
  useTriageContext,
} from "../../../store/contexts/TriageContext/TriageContext";
import FormStepper from "./Stepper/FormStepper";
import { IStep } from "./Stepper/models";
import TriageInfo from "../TriageInfo/TriageInfo";

const translationPath = "page.triageEmergencyDepartment.";

const NewTriage = () => {
  const { t } = useTranslation();
  const [beneficiaryId, setBeneficiaryId] = useState("");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { sendRequest } = useFetch(TriageEmergencyDepartment.create);

  const methods = useForm<TAppointment>({
    resolver: zodResolver(appointmentSchema),
    mode: "onChange",
  });

  const onSelectBeneficiary = useCallback(
    (name: string, individualId: string) => {
      setBeneficiaryId(individualId);
    },
    []
  );

  const STEPS: IStep<TAppointment>[] = [
    {
      label: `${t(translationPath + "select_beneficiary")}`,
      element: (
        <TriageSelectBeneficiary onSelectBeneficiary={onSelectBeneficiary} />
      ),
      fields: ["patient"],
    },
    {
      label: `${t(translationPath + "select_insurance")}`,
      element: <Insurance individualId={beneficiaryId} />,
      fields: ["insurance"],
    },
    {
      label: `${t(translationPath + "symptoms")}`,
      element: <TriageInfo />,
      fields: ["anamnesis"],
    },
  ];

  const { setStepsList } = useTriageContext();
  setStepsList(STEPS);

  const onSubmitHandler = useCallback(async () => {
    const valid = await methods.trigger(["anamnesis"]);
    if (!valid) return;
    const v = methods.getValues();
    const { success } = await sendRequest({
      anamnesis: v.anamnesis,
      individualId: v.patient.individualId ? v.patient.individualId : "",
      insuranceIntegrationId: v.insurance.insuranceIntegrationId,
    });
    if (success) {
      navigate(-1);
      dispatch(
        notificationSliceActions.showNotification({
          message: `${t(
            translationPath + "screening_successfully_registered"
          )}`,
          type: "success",
        })
      );
    }
  }, [sendRequest, navigate, dispatch, t, methods]);

  return (
    <TriageContextProvider>
      <PagesContainer title={t(translationPath + "service")}>
        <FormProvider<TAppointment> {...methods}>
          <FormStepper<TAppointment>
            steps={STEPS}
            finishLabel={t(translationPath + "save")!}
            onFinish={onSubmitHandler}
          />
        </FormProvider>
      </PagesContainer>
    </TriageContextProvider>
  );
};

export default NewTriage;
